import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Zap, Shield, Clock, Phone, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { ModernLogo } from "@/components/ModernLogo";
import electricianImage from "@/assets/real-electrician.jpg";

export const ElectricalPage = () => {
  return (
    <div className="min-h-screen bg-gradient-subtle">
      <SEO
        title="Professional Electrical Services - Certified Electricians | Sortable"
        description="Find qualified, NICEIC certified electricians near you. Electrical installations, rewiring, safety inspections, and emergency repairs across the UK. Fully insured professionals."
        keywords="electrician, electrical services, electrical installation, rewiring, electrical inspection, fuse box replacement, socket installation, lighting installation, emergency electrician, 24/7 electrician, NICEIC electrician, Part P electrician, electrical safety, electrical repair, electrical maintenance, electrical testing, local electrician, qualified electrician, certified electrician"
        canonical="https://sortable.uk/services/electrical"
        ogImage="https://sortable.uk/real-electrician.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Professional Electrical Services",
          "description": "Electrical installations and repairs by certified electricians",
          "provider": {
            "@type": "Organization",
            "name": "Sortable"
          },
          "serviceType": "Electrical Services",
          "areaServed": {
            "@type": "Country",
            "name": "United Kingdom"
          }
        }}
      />

      {/* Header */}
      <header className="bg-background/95 backdrop-blur-sm border-b border-border/50 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-2 md:py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <ModernLogo showTagline={false} />
            </Link>
            <div className="flex items-center gap-1 md:gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <span className="hidden md:inline">Home</span>
                  <span className="md:hidden">Home</span>
                </Button>
              </Link>
              <Link to="/for-tradesmen">
                <Button variant="outline" size="sm">
                  <span className="hidden md:inline">For Tradesmen</span>
                  <span className="md:hidden">Tradesmen</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 px-4">
        <div className="container mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-yellow-600/10 border border-yellow-600/20 text-yellow-600 px-6 py-3 rounded-full text-lg font-semibold mb-8 shadow-elegant">
            <Zap className="h-5 w-5" />
            Professional Electrical Services
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-hero bg-clip-text text-transparent">
            Certified Electricians & Electrical Contractors
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            From emergency electrical repairs to complete rewiring projects, find NICEIC certified electricians for safe, reliable electrical work. All professionals are fully qualified and insured.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/create-job">
              <Button size="lg" className="bg-gradient-hero hover:shadow-glow transform hover:scale-105">
                <Users className="mr-2 h-5 w-5" />
                Find Electricians Now
              </Button>
            </Link>
            <Link to="/for-tradesmen">
              <Button size="lg" variant="outline">
                <Zap className="mr-2 h-5 w-5" />
                Join as an Electrician
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 px-4 bg-background/50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Complete Electrical Services
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our network of certified electricians provides comprehensive electrical solutions for domestic and commercial properties.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Emergency Electrical Repairs",
                description: "24/7 emergency electrical repairs for power outages and electrical faults",
                icon: <Clock className="w-6 h-6" />
              },
              {
                title: "Full House Rewiring",
                description: "Complete rewiring services for older properties and safety upgrades",
                icon: <Zap className="w-6 h-6" />
              },
              {
                title: "Electrical Inspections",
                description: "EICR electrical safety certificates and condition reports",
                icon: <Shield className="w-6 h-6" />
              },
              {
                title: "Fuse Box Replacement",
                description: "Modern consumer unit installation and electrical panel upgrades",
                icon: <CheckCircle className="w-6 h-6" />
              },
              {
                title: "Socket & Switch Installation",
                description: "New electrical outlets, switches, and additional power points",
                icon: <Zap className="w-6 h-6" />
              },
              {
                title: "Lighting Installation",
                description: "Indoor and outdoor lighting design and installation services",
                icon: <Shield className="w-6 h-6" />
              }
            ].map((service, index) => (
              <Card key={index} className="border-border/50 hover:shadow-elegant transition-all duration-300">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-yellow-600/10 rounded-lg text-yellow-600">
                      {service.icon}
                    </div>
                    <CardTitle className="text-lg">{service.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {service.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose Our Electrical Network?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              All our electricians are NICEIC certified, fully insured, and committed to electrical safety standards.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">NICEIC Certified</h4>
                    <p className="text-muted-foreground">
                      All electricians are NICEIC approved contractors with Part P certification for domestic electrical work.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Safety Compliant</h4>
                    <p className="text-muted-foreground">
                      All work complies with BS 7671 wiring regulations and building control requirements.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">24/7 Emergency Response</h4>
                    <p className="text-muted-foreground">
                      Emergency electricians available for urgent electrical problems and safety hazards.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Warranty & Insurance</h4>
                    <p className="text-muted-foreground">
                      All work comes with warranty and comprehensive public liability insurance coverage.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src={electricianImage} 
                alt="Professional electrician working on electrical installation"
                className="rounded-lg shadow-lg w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Need an Electrician Today?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Post your electrical job and get quotes from certified, local electricians. Safe, reliable, and fully compliant electrical work guaranteed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/create-job">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="bg-white text-primary hover:bg-gray-100 font-semibold px-8 py-3 border-white"
                >
                  <Users className="mr-2 h-5 w-5" />
                  Get Quotes Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-background border-t py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <ModernLogo showTagline={false} />
              <p className="text-muted-foreground mt-4 max-w-md">
                Connecting you with NICEIC certified electricians across the UK for all your electrical needs.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li>Emergency Repairs</li>
                <li>House Rewiring</li>
                <li>Electrical Inspections</li>
                <li>Fuse Box Replacement</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Customers</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/" className="hover:text-primary">Home</Link></li>
                <li><Link to="/how-it-works-customer" className="hover:text-primary">How It Works</Link></li>
                <li><Link to="/create-job" className="hover:text-primary">Post a Job</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Electricians</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/for-tradesmen" className="hover:text-primary">Join Our Network</Link></li>
                <li><Link to="/tradesman-registration" className="hover:text-primary">Register Now</Link></li>
                <li><Link to="/how-it-works" className="hover:text-primary">How It Works</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};